import React, {useCallback, useEffect, useRef} from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import {usePrevNextButtons} from './EmblaCarouselArrowButtons'
import {useDotButton} from './EmblaCarouselDotButton'
import './base.css'
import './embla.css'
import {getFactionImagePath} from "../../utils/factions/getFactionImagePath";
import {addClassNameIfTrue} from "../../utils/other/addClassNameIfTrue";
import {checkIfColorNeedsDarkText} from "../../utils/other/checkIfColorNeedsDarkText";
import {sendButtonClickEvent} from "../../analytics/gameAnalytics";
import PageButton from "../elements/PageButton/PageButton";
import FactionCardOverlay from "../FactionCardOverlay/FactionCardOverlay";
import SkinRankMark from "../SkinRankMark/SkinRankMark";
import {MENU_SOUND_MANAGER} from "../../scenes/MenuSoundManager";

// Синхронная проверка (до первого рендера, не через ref/эффект): картинка с
// этим src уже в кэше браузера? Присвоение src Image() не запускает новую
// загрузку поверх уже закэшированной — .complete сразу true, если ресурс уже
// готов. Без этого — при повторном заходе на уже посещенную страницу
// компонент все равно на первом кадре рисовал loaded=false (эффект проверяет
// .complete только ПОСЛЕ commit/paint), и заглушка на мгновение мелькала
// поверх уже готовой картинки
const checkIfImageCached = (imgSrc) => {
  if (!imgSrc) return false;
  const probe = new Image();
  probe.src = imgSrc;
  return probe.complete;
};

/**
 * placeholderSrc (опционально): пока src не загрузился, вместо пустого места
 * показывается уже готовая (обычно предзагруженная и закэшированная где-то
 * еще на странице, например иконка таба) картинка-заглушка — она стоит в
 * потоке и резервирует размер блока, а сама src угасанием/проявлением
 * проступает поверх нее по загрузке (см. .loaded-image-* в embla.css).
 * Без placeholderSrc — прежнее поведение: одно изображение, фейд-ин без
 * резервирования места (годится там, где размер уже фиксирован CSS).
 */
export function LoadedImage({src, alt = '', className, placeholderSrc, title}) {
  const [loaded, setLoaded] = React.useState(() => checkIfImageCached(src));
  const imgRef = useRef(null);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setLoaded(true);
    }
  }, []);

  if (!placeholderSrc) {
    return (
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        title={title}
        className={`${className} ${loaded ? 'embla__slide__faction-image--loaded' : ''}`}
        onLoad={() => setLoaded(true)}
        loading="eager"
        decoding="async"
        style={{
          opacity: loaded ? 1 : 0,
        }}
      />
    );
  }

  return (
    <span className="loaded-image-wrapper" title={title}>
      <img
        src={placeholderSrc}
        alt=""
        aria-hidden="true"
        className={`${className} loaded-image-placeholder ${loaded ? 'loaded-image-placeholder_hidden' : ''}`}
        loading="eager"
        decoding="async"
      />
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`${className} loaded-image-main ${loaded ? 'loaded-image-main_visible' : ''}`}
        onLoad={() => setLoaded(true)}
        loading="eager"
        decoding="async"
      />
    </span>
  );
}

/**
 * Тот же принцип «загрузилось ли изображение», что и внутри LoadedImage
 * выше, но для случаев, где сама картинка не рендерится как <img> (например,
 * используется только как CSS mask-image — см. FactionSkinInventoryItem
 * .inventory-skin-icon_tinted в InventoryPage.js) — там нет своего тега с
 * onLoad, поэтому статус загрузки отслеживается отдельным зондом-Image()
 */
export function useIsImageLoaded(src) {
  const [loaded, setLoaded] = React.useState(() => checkIfImageCached(src));

  useEffect(() => {
    if (!src) {
      setLoaded(false);
      return;
    }
    if (checkIfImageCached(src)) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (!cancelled) setLoaded(true);
    };
    probe.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  return loaded;
}

const TWEEN_FACTOR_BASE = 0.52

// Минимальное число слайдов, при котором embla стабильно зацикливается
// (см. дублирование массива элементов ниже)
const MIN_LOOP_SLIDES_COUNT = 6

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max)

const PAGE_ID = 'FactionsCarousel';

const FactionsCarousel = (props) => {
  const {
    factions,
    items,
    options,
    onSelect,
    checkIfSelectionAvailable,
    onSelectedIndexChange,
    ritualPhase,
    onRitualStart,
    getItemImagePath,
    getItemImageElement,
    getItemName,
    getItemDescription,
    getItemTextColor,
    getItemImageContainerStyle,
    getItemNameClassName,
    getItemOverlay,
    // Рамка поверх картинки слайда: рейтинговым образам (см. RankSkinFrame)
    getItemFramePath,
    // Рейтинг обличия на слайде — шевроны в правом нижнем углу картинки (см.
    // SkinRankMark). Только явным резолвером: карусель образов отрядов
    // (FactionUnitSkinsCarousel) его не передает, и шевронов у нее нет
    getItemSkinRank,
    isItemAvailable,
    checkIfDetailsAvailable,
    selectButtonLabel,
    isSelectDisabled,
    disableMaskLabel,
    showDescription = true,
    showDetailsButton = true,
    pageId = PAGE_ID,
    selectButtonId = 'faction_select',
    selectButtonNeutralColor = false,
    renderSelectButton,
    className,
  } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const tweenFactor = useRef(0)
  const tweenNodes = useRef([])
  const ritualPhaseRef = useRef(ritualPhase);

  useEffect(() => {
    ritualPhaseRef.current = ritualPhase;
  }, [ritualPhase]);

  const [isVisible, setIsVisible] = React.useState(false);
  const [showCardOverlay, setShowCardOverlay] = React.useState(false);
  useEffect(() => {
    setIsVisible(true)
  }, []);

  const {
    selectedIndex,
  } =
    useDotButton(emblaApi)

  useEffect(() => {
    onSelectedIndexChange?.(selectedIndex);
  }, [selectedIndex, onSelectedIndexChange]);

  const {
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  // Embla (loop:true) ломает верстку при малом числе слайдов: зацикливание
  // включается, только если ВСЕ слайды, кроме одного, вместе перекрывают
  // ширину вьюпорта (canLoop в embla-carousel), иначе loop молча выключается —
  // и соседние карточки перестают отрисовываться/масштабироваться.
  // При слайде min(90%, 320px) и вьюпорте до 960px (embla.css) четыре слайда
  // дают ровно 3 * 320 = 960 — впритык, и на широких экранах проверка не
  // проходит: у фракций с одним типом отряда (великаны, драконы, феникс, духи)
  // карусель отображалась криво, а у гремлинов с их тремя (то есть шестью
  // после дублирования) — нормально. Поэтому дублируем исходный массив, пока
  // слайдов не станет MIN_LOOP_SLIDES_COUNT и более, с запасом.
  // Дублирование удваивает массив целиком (все элементы представлены поровну)
  // и на поведение не влияет: выбор идет по индексу текущего слайда, а масштаб
  // соседей нормирован на число слайдов (см. setTweenFactor)
  let dataArray = items || factions;
  while (dataArray.length > 0 && dataArray.length < MIN_LOOP_SLIDES_COUNT) {
    dataArray = [...dataArray, ...dataArray];
  }
  const slides = Array.from(Array(dataArray.length).keys());

  const setTweenNodes = useCallback((emblaApi) => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector('.embla__slide__number')
    })
  }, [])

  const setTweenFactor = useCallback((emblaApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.snapList().length
  }, [])

  const tweenScale = useCallback((emblaApi, event) => {
    if (ritualPhaseRef.current) return;

    const engine = emblaApi.internalEngine()
    const scrollProgress = emblaApi.scrollProgress()
    const slidesInView = emblaApi.slidesInView()
    const isScrollEvent = event?.type === 'scroll'

    emblaApi.snapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress
      const slidesInSnap = engine.scrollSnapList.slidesBySnap[snapIndex]

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target()

            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target)

              if (sign === -1) {
                diffToTarget = scrollSnap - (1 + scrollProgress)
              }
              if (sign === 1) {
                diffToTarget = scrollSnap + (1 - scrollProgress)
              }
            }
          })
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current)
        const scale = numberWithinRange(tweenValue, 0, 1).toString()
        const tweenNode = tweenNodes.current[slideIndex]
        tweenNode.style.transform = `scale(${scale})`
        tweenNode.style.opacity = `${scale}`
      })
    })
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    setTweenNodes(emblaApi)
    setTweenFactor(emblaApi)
    tweenScale(emblaApi)

    emblaApi
      .on('reinit', setTweenNodes)
      .on('reinit', setTweenFactor)
      .on('reinit', tweenScale)
      .on('scroll', tweenScale)
      .on('slidefocus', tweenScale)

    return () => {
      emblaApi
        .off('reinit', setTweenNodes)
        .off('reinit', setTweenFactor)
        .off('reinit', tweenScale)
        .off('scroll', tweenScale)
        .off('slidefocus', tweenScale)
    }
  }, [emblaApi, setTweenFactor, setTweenNodes, tweenScale])

  const onSelectFinal = () => {
    const selectedItem = dataArray[selectedIndex];
    let isSelectionAvailable = true;
    if (checkIfSelectionAvailable) {
      isSelectionAvailable = checkIfSelectionAvailable(selectedItem);
    }

    if (!isSelectionAvailable) return;

    sendButtonClickEvent(pageId, selectButtonId, selectedItem.id);

    if (onRitualStart) {
      onRitualStart(selectedItem);
    } else if (onSelect) {
      onSelect(selectedItem)
    }
  }

  const selectedItem = dataArray[selectedIndex];
  const ritualColor = getItemTextColor
    ? getItemTextColor(selectedItem)
    : selectedItem?.textColor || '#fff';
  const selectedImplemented = isItemAvailable
    ? isItemAvailable(selectedItem)
    : selectedItem?.implemented;
  const selectedTextColor = getItemTextColor
    ? getItemTextColor(selectedItem)
    : selectedItem?.textColor;
  const selectedDisabled = isSelectDisabled
    ? isSelectDisabled(selectedItem)
    : !selectedImplemented;
  // Кнопка выбора красится цветом элемента (образа, фракции), кроме
  // заблокированной и нейтральной. На ярком цвете — желтом, оранжевом,
  // голубом — светлый текст кнопки не читается, там он темный
  const selectButtonColor = (selectedDisabled || selectButtonNeutralColor) ? undefined : selectedTextColor;
  const selectButtonDarkText = checkIfColorNeedsDarkText(selectButtonColor);
  // Отдельно от общей доступности пункта: у некоторых элементов (например,
  // моковой "Случайной фракции" в FactionSelectPage) нет реального конфига
  // фракции, поэтому карточка деталей (FactionCardOverlay) для них крашится —
  // кнопка "Подробно" для них блокируется через этот проп, по умолчанию
  // ведет себя как раньше (совпадает с selectedImplemented)
  const selectedDetailsAvailable = checkIfDetailsAvailable
    ? checkIfDetailsAvailable(selectedItem)
    : selectedImplemented;

  const rootClass = [
    'embla',
    isVisible ? 'embla-visible' : '',
    ritualPhase ? 'embla--ritual-active' : '',
    ritualPhase ? `embla--ritual-${ritualPhase}` : '',
    className,
  ].filter(Boolean).join(' ');

  const onFactionCardOverlayClose = () => {
    MENU_SOUND_MANAGER.playSideMenuClick();
    setShowCardOverlay(false);
  }

  const getSelectButtonLabel = () => {
    if (!selectButtonLabel) return 'Выбрать';
    return typeof selectButtonLabel === 'function'
      ? selectButtonLabel(selectedItem)
      : selectButtonLabel;
  };

  return (
    <>
      {ritualPhase && (
        <div
          className={`ritual-overlay ritual-overlay--${ritualPhase}`}
          style={{'--ritual-color': ritualColor}}
        />
      )}
      <div className={rootClass} style={{'--ritual-color': ritualColor}}>
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((index) => {
              const item = dataArray[index];
              const id = item.id;
              const name = getItemName ? getItemName(item) : item.name;
              const textColor = getItemTextColor ? getItemTextColor(item) : item.textColor;
              const imageContainerStyle = getItemImageContainerStyle ? getItemImageContainerStyle(item) : undefined;
              const nameClassName = getItemNameClassName ? getItemNameClassName(item) : '';
              const description = showDescription
                ? (getItemDescription ? getItemDescription(item) : item.description)
                : null;
              const implemented = isItemAvailable
                ? isItemAvailable(item)
                : item.implemented;
              const imagePath = getItemImagePath
                ? getItemImagePath(item)
                : getFactionImagePath(id);
              // Рамка рейтингового образа: отдельный слой, а не getItemOverlay
              // — тот занят разовыми накладками (ошибка покупки, рассказ про
              // лигу), а рамка висит на образе всегда
              const framePath = getItemFramePath?.(item);

              const isSelected = index === selectedIndex;
              // По индексу, а не по id: при задвоении массива (см. dataArray выше)
              // два разных слайда могут делить один id — ритуал должен подсвечивать
              // ровно тот слайд, что сейчас выбран, а не оба его дубликата разом
              return (
                <div
                  className={`embla__slide ${isSelected ? 'embla__slide--selected' : ''} ${isSelected ? 'embla__slide--chosen-faction' : ''}`}
                  key={index}
                >
                  <div className="embla__slide__number">
                    <div
                      className={`embla_faction-name ${nameClassName}`}
                      style={{color: textColor}}
                    >
                      {name}
                    </div>

                    <div className={'embla__slide__faction-image-container'} style={imageContainerStyle}>
                      {/* Свой элемент вместо картинки; пустой ответ —
                          обычная картинка слайда */}
                      {getItemImageElement?.(item) || (
                        <LoadedImage
                          className={'embla__slide__faction-image'}
                          src={imagePath}
                          alt={name}
                        />
                      )}
                      {!implemented && (
                        <div className={'embla_faction-disable-mask'}>
                          {typeof disableMaskLabel === 'function'
                            ? disableMaskLabel(item)
                            : (disableMaskLabel || 'В разработке')}
                        </div>
                      )}
                      {framePath && (
                        <img
                          className={'embla__slide__faction-frame'}
                          src={framePath}
                          alt={''}
                          aria-hidden={'true'}
                        />
                      )}
                      {/* До разовых накладок: те непрозрачны и закрывают
                          слайд целиком вместе с шевронами */}
                      <SkinRankMark
                        skinRank={getItemSkinRank?.(item)}
                        className={'embla__slide__skin-rank'}
                      />
                      {getItemOverlay?.(item)}
                    </div>

                    {description && (
                      <div className={'embla_faction-description'}>
                        {description}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className={`embla__buttons ${ritualPhase ? 'embla__buttons--ritual-fading' : ''}`}>
        <button
          className="embla_arrow-button" onClick={onPrevButtonClick}
        >
          <img
            className={'embla_arrow-button-img'}
            src={'/img/icons/arrow left.png'}
            alt={'left'}
          />
        </button>
        {renderSelectButton?.(selectedItem) || (
          <PageButton
            className={`embla_select-button ${addClassNameIfTrue(selectButtonNeutralColor, 'embla_select-button--neutral')} ${addClassNameIfTrue(selectButtonDarkText, 'embla_select-button--dark-text')} ${addClassNameIfTrue(selectedDisabled, 'faction-disabled')}`}
            onClick={onSelectFinal}
            style={{
              opacity: selectedDisabled ? 0.5 : 1,
              backgroundColor: selectButtonColor,
            }}
          >
            {getSelectButtonLabel()}
          </PageButton>
        )}
        <button
          className="embla_arrow-button" onClick={onNextButtonClick}
        >
          <img
            className={'embla_arrow-button-img'}
            src={'/img/icons/arrow right.png'}
            alt={'left'}
          />
        </button>
      </div>

      {showDetailsButton && selectedItem && (
        <div className={`embla__details-button-container ${ritualPhase ? 'embla__buttons--ritual-fading' : ''}`}>
          <PageButton
            className={`main-menu-button embla_details-button ${addClassNameIfTrue(!selectedDetailsAvailable, 'faction-disabled')}`}
            onClick={() => setShowCardOverlay(true)}
            pageId={pageId}
            buttonId="show_faction_details"
            disabled={!selectedDetailsAvailable}
          >
            Подробно
          </PageButton>
        </div>
      )}

      {showDetailsButton && (
        <FactionCardOverlay
          factionId={showCardOverlay ? selectedItem?.id : null}
          skinId={showCardOverlay ? selectedItem?.skinId : null}
          onClose={onFactionCardOverlayClose}
        />
      )}
    </>
  )
}

export default FactionsCarousel
